import get from 'lodash/get';
import {useEffect,useState} from 'react';
import {useNavigate,useParams,useSearchParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import { Helmet } from 'react-helmet';

import Container from '@mui/material/Container';

import {formatProviderName,isPersonalTrainer} from '../../helpers/providers';
import {reset} from '../../redux/reducers/newAppointmentForm';
import {useGetHighlightsQuery} from '../../redux/services/providerHighlightApi';
import {useGetProvidersQuery} from '../../redux/services/providerApi';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {useGetReviewsQuery} from '../../redux/services/reviewApi';
import {useCreateAppointmentMutation} from '../../redux/services/appointmentApi';

import AppDashboard from '../AppDashboard';
import NewAppointmentForm from '../../components/scheduling/NewAppointmentForm';
import ExistingAccountDialog from '../../components/scheduling/ExistingAccountDialog';
import PublicBookingWithProviderPage from '../booking/PublicBookingWithProviderPage';

const handleSubmit = ({
  createAppointment,
  onSuccess,
  setError,
  resetForm,
  isNewCustomer,
  navigate,
  providerId,
}) => formValues => {
  if (isNewCustomer) {
    navigate(`/signup?alreadyScheduled=true&selectedProviderId=${providerId}`);
    return;
  }
  const res = createAppointment(formValues);
  if (res.error) return setError({message: 'Error scheduling appointment.'});
  navigate('/');
  resetForm();
};

const NewAppointmentPage = props => {
  const [error, setError] = useState(null);
  const [isResetting, setIsResetting] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {id,slug} = useParams();
  const [searchParams] = useSearchParams();
  const [dialogIsOpen, setDialogIsOpen] = useState(true);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const version = searchParams.get('version');
  // reset form when the page loads to ensure a clean starting state
  useEffect(() => {
    dispatch(reset());
    setIsResetting(false);
  }, []);
  // Setup API queries...
  const providerQuery = useGetProvidersQuery(
    slug ? {slug, is_hidden: false} : {id, is_hidden: false}, 
    {skip: !id && !slug}
  );
  const provider = get(providerQuery, 'data[0]');
  const sessionTypesQuery = useGetSessionTypesQuery(
    {providerId: provider?.id},
    {skip: !provider?.id}
  );
  const highlightsQuery = useGetHighlightsQuery(null, {skip: false});
  const reviewsQuery = useGetReviewsQuery({provider_id: provider?.id}, {skip: !provider?.id});
  const [createAppointment] = useCreateAppointmentMutation();
  // pass data and handlers into the component
  const childProps = {
    ...props,
    onSubmit: handleSubmit({
      createAppointment,
      setError,
      isNewCustomer,
      resetForm: () => dispatch(reset()),
      navigate,
      providerId: provider?.id,
    }),
    highlights: get(highlightsQuery, 'data', []) || [],
    reviews: get(reviewsQuery, 'data', []) || [],
    selectedProvider: provider,
    sessionTypes: get(sessionTypesQuery, 'data', []),
    isProvider: false,
    hideStepper: true,
    selectedProviderId: provider?.id ? parseInt(provider.id, 10) : null,
    loading: providerQuery.isLoading || sessionTypesQuery.isLoading,
    error: providerQuery.error || sessionTypesQuery.error,
    showProviderInfo: true,
    isNewPatient: true,
  };
  const providerName = formatProviderName(get(childProps, 'selectedProvider', ''));
  const city = get(childProps, 'selectedProvider.primary_location', '');
  const providerType = isPersonalTrainer(childProps?.selectedProvider) ? 'Personal Trainer' : 'Physical Therapist';
  const metaTitle = `Schedule with ${providerName} - MovementX ${providerType} in ${city}`;
  const metaDescription = `Schedule an appointment with ${providerName}, a MovementX ${providerType} in ${city}.`;
  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name='description' content={metaDescription} />
      </Helmet>
      <Container maxWidth='sm'>
        {childProps.selectedProvider && 
          <ExistingAccountDialog 
            isOpen={dialogIsOpen} 
            onClose={() => setDialogIsOpen(false)}
            provider={childProps.selectedProvider}
            onIsNewAccount={() => setIsNewCustomer(true)}
          />
        }
        {
          !isResetting && <NewAppointmentForm {...childProps} />
        }
      </Container>
    </>
  );
};

const Router = props => {
  const {id,slug} = useParams();
  const [search] = useSearchParams();
  return (
    search.get('version') !== 'v1'
    ? <PublicBookingWithProviderPage {...props} />
    : <NewAppointmentPage {...props} />
  );
};

export default Router;
